import { i18n } from './i18n.js';
import { LOCALE_META, type AppLocale, normalizeLocale } from './constants.js';
import { RequestStatus, SLA_ERR_HOURS, SLA_WARN_HOURS } from '@magimp/shared';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const EMPTY = '—';

function currentLocale(locale?: AppLocale): AppLocale {
  return locale ?? normalizeLocale(i18n.language);
}

function intlLocale(locale?: AppLocale): string {
  return LOCALE_META[currentLocale(locale)].intlLocale;
}

function normalizeSpaces(value: string): string {
  return value.replace(/[\u00A0\u202F]/g, ' ');
}

type UnitKey =
  | 'byte'
  | 'cubicMeterShort'
  | 'dayShort'
  | 'hourShort'
  | 'kilogramShort'
  | 'kilobyte'
  | 'megabyte'
  | 'minuteShort';

function unit(key: UnitKey): string {
  return i18n.t(`common:units.${key}`);
}

export function formatNumber(value: number, locale?: AppLocale): string {
  return normalizeSpaces(new Intl.NumberFormat(intlLocale(locale)).format(value));
}

/** The locale's decimal separator, read from `Intl` rather than hardcoded. */
function decimalSeparator(locale?: AppLocale): string {
  const part = new Intl.NumberFormat(intlLocale(locale))
    .formatToParts(0.1)
    .find((p) => p.type === 'decimal');
  return normalizeSpaces(part?.value ?? '.');
}

/**
 * Group the thousands of a `\d+\.\d{2}` money STRING for display WITHOUT a
 * lossy float conversion. The integer part is grouped via `Intl` on a `BigInt`
 * (exact for any magnitude) and the exact 2 fraction digits are appended
 * verbatim, so a high-precision `Decimal(12,2)` value never rounds. Returns the
 * input unchanged if it is not in canonical `\d+\.\d{2}` shape.
 */
export function formatMoneyString(value: string, locale?: AppLocale): string {
  if (!/^\d+\.\d{2}$/.test(value)) return value;
  const dot = value.indexOf('.');
  const intPart = value.slice(0, dot);
  const fracPart = value.slice(dot + 1);
  const grouped = normalizeSpaces(
    new Intl.NumberFormat(intlLocale(locale)).format(BigInt(intPart)),
  );
  return `${grouped}${decimalSeparator(locale)}${fracPart}`;
}

// WHY (#1304): request weight/volume persist at `Decimal(13,4)`, so display
//   must surface up to 4 fraction digits — a value like `0,0001` rounded to
//   the previous 3dp (weight) / 2dp (volume) would read as `0`. Trailing
//   zeros stay trimmed (`minimumFractionDigits: 0`) so whole values render
//   without padding.
export function formatWeight(kg: number | null, locale?: AppLocale): string {
  if (kg == null) return EMPTY;
  const formatted = normalizeSpaces(
    new Intl.NumberFormat(intlLocale(locale), {
      maximumFractionDigits: 4,
    }).format(kg),
  );
  return `${formatted} ${unit('kilogramShort')}`;
}

export function formatVolume(m3: number | null, locale?: AppLocale): string {
  if (m3 == null) return EMPTY;
  const formatted = normalizeSpaces(
    new Intl.NumberFormat(intlLocale(locale), {
      minimumFractionDigits: 1,
      maximumFractionDigits: 4,
    }).format(m3),
  );
  return `${formatted} ${unit('cubicMeterShort')}`;
}

export function formatRub(value: number | null, locale?: AppLocale): string {
  if (value == null) return EMPTY;
  return normalizeSpaces(
    new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency: 'RUB',
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    }).format(value),
  );
}

/**
 * Compact RUB for KPI cards (#602 acceptance criteria 38/233/589: KPI
 * cards use Intl.NumberFormat with `notation: 'compact'`,
 * `compactDisplay: 'short'`). Tables and tooltips keep `formatRub`'s
 * exact formatting.
 */
export function formatRubCompact(value: number | null, locale?: AppLocale): string {
  if (value == null) return EMPTY;
  return normalizeSpaces(
    new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency: 'RUB',
      currencyDisplay: 'narrowSymbol',
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    }).format(value),
  );
}

export function formatMoney(
  value: number | null,
  currency: 'USD' | 'RUB' | 'CNY',
  locale?: AppLocale,
): string {
  if (value == null) return EMPTY;
  return normalizeSpaces(
    new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency,
      currencyDisplay: currency === 'RUB' ? 'narrowSymbol' : 'symbol',
      maximumFractionDigits: 2,
    }).format(value),
  );
}

export function formatSize(bytes: number, locale?: AppLocale): string {
  if (bytes < 1024) return `${formatNumber(bytes, locale)} ${unit('byte')}`;
  if (bytes < 1024 * 1024) {
    return `${formatNumber(Math.round(bytes / 1024), locale)} ${unit('kilobyte')}`;
  }
  const formatted = normalizeSpaces(
    new Intl.NumberFormat(intlLocale(locale), {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(bytes / 1024 / 1024),
  );
  return `${formatted} ${unit('megabyte')}`;
}

export function formatDateTime(
  ts: number | string | null,
  locale?: AppLocale,
  options?: { timeZone?: string },
): string {
  if (!ts) return EMPTY;
  const date = typeof ts === 'string' ? new Date(ts) : new Date(ts);
  if (Number.isNaN(date.getTime())) return String(ts);
  return normalizeSpaces(
    new Intl.DateTimeFormat(intlLocale(locale), {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: options?.timeZone,
    }).format(date),
  );
}

function formatClock(ts: number, locale?: AppLocale): string {
  return normalizeSpaces(
    new Intl.DateTimeFormat(intlLocale(locale), {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ts)),
  );
}

export function formatRelative(
  ts: number | null,
  now: number = Date.now(),
  locale?: AppLocale,
): string {
  if (!ts) return EMPTY;
  const appLocale = currentLocale(locale);
  const diff = now - ts;
  if (diff < MIN) return i18n.t('requests:format.justNow');
  const rtf = new Intl.RelativeTimeFormat(intlLocale(appLocale), { numeric: 'auto' });
  if (diff < HOUR) return normalizeSpaces(rtf.format(-Math.floor(diff / MIN), 'minute'));
  if (diff < DAY) {
    if (diff < 6 * HOUR) return normalizeSpaces(rtf.format(-Math.floor(diff / HOUR), 'hour'));
    return formatClock(ts, appLocale);
  }
  if (diff < 2 * DAY) {
    return i18n.t('requests:format.yesterdayAt', { time: formatClock(ts, appLocale) });
  }
  if (diff < 7 * DAY) return normalizeSpaces(rtf.format(-Math.floor(diff / DAY), 'day'));
  return normalizeSpaces(
    new Intl.DateTimeFormat(intlLocale(appLocale), {
      day: 'numeric',
      month: 'short',
    }).format(new Date(ts)),
  );
}

export type SLALevel = 'none' | 'ok' | 'warn' | 'err' | 'success';

export interface SLAInfo {
  text: string;
  level: SLALevel;
}

function compactDuration(ms: number, locale?: AppLocale): string {
  const h = Math.floor(ms / HOUR);
  const m = Math.floor((ms % HOUR) / MIN);
  if (h > 0)
    return `${formatNumber(h, locale)} ${unit('hourShort')} ${formatNumber(m, locale)} ${unit('minuteShort')}`;
  return `${formatNumber(m, locale)} ${unit('minuteShort')}`;
}

// INVARIANT (#962): SLA display lifecycle matches backend active-only
//   semantics. DRAFT and RETURN are pre/inter-work — hidden. READY_FOR_WORK
//   and IN_WORK are LIVE (countdown vs `slaDeadline`). PROCESSED, SUCCESS,
//   REFUSED, CANNOT_TRANSPORT, CLOSED, ARCHIVE are HISTORICAL — completion
//   duration computed against `processedAt ?? closedAt` (the
//   logistics-completion timestamp
//   #960 stamps on `IN_WORK -> PROCESSED`; PROCESSED finalisation lands
//   on the wire row and survives through subsequent terminal transitions).
//   The historical branch never restarts the countdown — once a row is
//   past PROCESSED, the chip stops ticking even if `slaDeadline` has not
//   yet been reached in wall-clock time.
const HISTORICAL_STATUSES: ReadonlySet<RequestStatus> = new Set([
  RequestStatus.PROCESSED,
  RequestStatus.SUCCESS,
  RequestStatus.REFUSED,
  RequestStatus.CANNOT_TRANSPORT,
  RequestStatus.CLOSED,
  RequestStatus.ARCHIVE,
]);

export function formatSLA(
  row: {
    status: RequestStatus;
    slaDeadline: number | null;
    inWorkAt: number | null;
    closedAt: number | null;
    processedAt: number | null;
  },
  now: number = Date.now(),
  locale?: AppLocale,
): SLAInfo {
  const { status, slaDeadline, inWorkAt, closedAt, processedAt } = row;

  if (status === RequestStatus.DRAFT || status === RequestStatus.RETURN) {
    return { text: EMPTY, level: 'none' };
  }

  if (HISTORICAL_STATUSES.has(status)) {
    // INVARIANT: completion presentation needs `inWorkAt` (work-start
    // anchor for the on-time text) plus a completion timestamp
    // (`processedAt` preferred — that is the logistics-completion
    // moment — falling back to `closedAt` for rows that bypassed
    // PROCESSED, or legacy rows that predate the #960 backfill).
    // `slaDeadline` is required to compute the breach delta. Any null
    // collapses to `—` so legacy rows do not render misleading numbers.
    const completionAt = processedAt ?? closedAt;
    if (!slaDeadline || !completionAt || !inWorkAt) {
      return { text: EMPTY, level: 'none' };
    }
    if (completionAt <= slaDeadline) {
      const value = compactDuration(completionAt - inWorkAt, locale);
      return { text: i18n.t('requests:format.completedIn', { value }), level: 'success' };
    }
    const value = compactDuration(completionAt - slaDeadline, locale);
    return { text: i18n.t('requests:format.overdue', { value }), level: 'err' };
  }

  // Live countdown — READY_FOR_WORK and IN_WORK.
  //
  // INVARIANT: defensive guard for edge case 8 — `inWorkAt` and
  // `slaDeadline` are written atomically by the SEND transition, but
  // legacy rows (or any future write path that forgets one) must not
  // render a misleading countdown.
  if (!slaDeadline || !inWorkAt) {
    return { text: EMPTY, level: 'none' };
  }
  const diff = slaDeadline - now;
  if (diff <= 0) {
    const value = compactDuration(Math.abs(diff), locale);
    return { text: i18n.t('requests:format.overdue', { value }), level: 'err' };
  }
  if (diff < SLA_ERR_HOURS * HOUR) {
    return {
      text: i18n.t('requests:format.remaining', { value: compactDuration(diff, locale) }),
      level: 'err',
    };
  }
  if (diff < SLA_WARN_HOURS * HOUR) {
    return {
      text: i18n.t('requests:format.remaining', { value: compactDuration(diff, locale) }),
      level: 'warn',
    };
  }
  return {
    text: i18n.t('requests:format.remaining', { value: compactDuration(diff, locale) }),
    level: 'ok',
  };
}

export const TIME = { MIN, HOUR, DAY } as const;
